import type { WorkItem, WorkLanesResult } from '~/features/home/work/types';

import { getApiErrorMessage } from '~/common/errors/getApiErrorMessage';
import { mapDocumentItem } from '~/features/home/work/workLanes/mapDocumentItem';
import { mapRequestItem } from '~/features/home/work/workLanes/mapRequestItem';

import type {
  HomeWorkLane,
  HomeWorkRequest,
  HomeWorkResponse,
} from './postHomeWork';

import { postHomeWork } from './postHomeWork';

/** A failed lane fetch, as a serializable RTK Query error. */
export interface HomeWorkLanesError {
  status: 'CUSTOM_ERROR';
  error: 'invalid_scope' | 'documents_unsupported' | 'request_failed';
  data?: string;
}

const NO_LANES: WorkLanesResult = { lanes: {}, failedLanes: [] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function responseOf(error: unknown): Record<string, unknown> | undefined {
  if (!isRecord(error)) return undefined;
  const response = error.response;
  return isRecord(response) ? response : undefined;
}

function statusOf(error: unknown): number | undefined {
  const status = responseOf(error)?.status;
  return typeof status === 'number' ? status : undefined;
}

function isInvalidScopeQuery(result: PromiseSettledResult<HomeWorkResponse>) {
  if (result.status !== 'rejected') return false;
  const r = responseOf(result.reason);
  const data = r?.data;
  return (
    r?.status === 400 && isRecord(data) && data.code === 'invalid_scope_query'
  );
}

function isUnsupported(result: PromiseSettledResult<HomeWorkResponse>) {
  return result.status === 'rejected' && statusOf(result.reason) === 422;
}

/** Every Your Work lane in a plan, as an RTK Query result. */
export async function getHomeWorkLanes(
  plan: HomeWorkRequest[],
  signal?: AbortSignal,
) {
  if (plan.length === 0) {
    return { data: NO_LANES };
  }

  const settled = await Promise.allSettled(
    plan.map((body) => postHomeWork(body, signal)),
  );

  if (settled.some(isInvalidScopeQuery)) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'invalid_scope',
      } satisfies HomeWorkLanesError,
    };
  }

  if (settled.some(isUnsupported)) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'documents_unsupported',
      } satisfies HomeWorkLanesError,
    };
  }

  const unauthorized = settled.find(
    (result) => result.status === 'rejected' && statusOf(result.reason) === 401,
  );
  if (unauthorized?.status === 'rejected') {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'request_failed',
        data: getApiErrorMessage(unauthorized.reason),
      } satisfies HomeWorkLanesError,
    };
  }

  if (settled.every((result) => result.status === 'rejected')) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'request_failed',
        data: getApiErrorMessage(settled[0].reason),
      } satisfies HomeWorkLanesError,
    };
  }

  const lanes: WorkLanesResult['lanes'] = {};
  const failedLanes: HomeWorkLane[] = [];

  settled.forEach((result, index) => {
    const { lane } = plan[index];
    if (result.status === 'rejected') {
      failedLanes.push(lane);
      return;
    }

    const items: WorkItem[] = [];
    for (const item of result.value.items) {
      if (item.kind === 'request') {
        const mapped = mapRequestItem(item, lane);
        if (!mapped) {
          console.warn(
            'Dropping unmappable Your Work request',
            item.ticket_id,
            item.action_date,
          );
          continue;
        }
        items.push(mapped);
        continue;
      }

      const mapped = mapDocumentItem(item, lane);
      if (!mapped) {
        console.warn(
          'Dropping unmappable Your Work document',
          item.document_id,
          item.action_date,
          item.work_type,
          item.document_handlers.length,
        );
        continue;
      }
      items.push(mapped);
    }

    lanes[lane] = {
      items,
      total: result.value.total,
      documentTotal: result.value.document_total,
      requestTotal: result.value.request_total,
      sources: result.value.sources,
    };
  });

  return { data: { lanes, failedLanes } };
}
